import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import {
  ShiftRateNegotiationRole,
  ShiftRateNegotiationStatus,
} from "@src/appV2/OpenShifts/RateNegotiation/types";
import { type BackgroundCheckMissingDataType } from "@src/appV2/OpenShifts/ShiftAction/types";
import { Button } from "@src/appV2/redesign/components/Button";
import { useRequiresBreakPolicyAcknowledgement } from "@src/appV2/redesign/Shift/Booking/useRequiresBreakPolicyAcknowledgement";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { useHistory } from "react-router-dom";

import { SHIFT_DISCOVERY_SHIFT_NEGOTIATION_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { type NavigateToModalFunction } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { createBackgroundCheckUrlFromMissingDataTypes } from "../createBackgroundCheckUrlFromMissingDataTypes";
import { type RateNegotiationToolLocationState } from "../types";

interface RateNegotiationActionProps {
  shiftId: string;
  workplaceId: string;
  rateNegotiationStatus?: ShiftRateNegotiationStatus;
  rateNegotiationLastOfferBy?: ShiftRateNegotiationRole;
  rateNegotiationLastOfferPayRate?: number;
  navigateToModal: NavigateToModalFunction;
  rateNegotiationId?: string;
  missingBackgroundCheckDataTypes: BackgroundCheckMissingDataType[];
}

export function RateNegotiationAction(props: RateNegotiationActionProps) {
  const {
    rateNegotiationStatus,
    rateNegotiationLastOfferBy,
    rateNegotiationLastOfferPayRate,
    shiftId,
    workplaceId,
    rateNegotiationId,
    navigateToModal,
    missingBackgroundCheckDataTypes = [],
  } = props;

  const history = useHistory<RateNegotiationToolLocationState>();
  const breakPolicyModalState = useModalState();

  const { requiresBreakPolicyAcknowledgement, breakPolicyNoteId, isLoading } =
    useRequiresBreakPolicyAcknowledgement({
      workplaceId,
      action: NoteAcknowledgementAction.START_RATE_NEGOTIATION,
    });

  function getNegotiationButtonText() {
    if (!isDefined(rateNegotiationStatus)) {
      return "Negotiate rate";
    }

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (rateNegotiationStatus) {
      case ShiftRateNegotiationStatus.OPEN: {
        if (
          rateNegotiationLastOfferBy === ShiftRateNegotiationRole.WORKPLACE &&
          isDefined(rateNegotiationLastOfferPayRate)
        ) {
          return `Reply to proposal: ${formatDollarsAsUsd(rateNegotiationLastOfferPayRate, {
            minimumFractionDigits: 0,
          })}/hr`;
        }

        return "View proposal";
      }

      case ShiftRateNegotiationStatus.EXPIRED: {
        return "Proposal expired";
      }

      case ShiftRateNegotiationStatus.REJECTED: {
        if (rateNegotiationLastOfferBy === ShiftRateNegotiationRole.WORKPLACE) {
          return "Proposal rejected";
        }

        return undefined;
      }

      default: {
        return undefined;
      }
    }
  }

  const label = getNegotiationButtonText();

  if (!isDefined(label) || isLoading) {
    return null;
  }

  function navigateToRateNegotiationModal() {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_NEGOTIATION_MODAL_PATH, {
      pathParams: { shiftId },
      searchParams: rateNegotiationId ? { rateNegotiationId } : {},
    });
  }

  const isDisabled =
    rateNegotiationStatus === ShiftRateNegotiationStatus.EXPIRED ||
    rateNegotiationStatus === ShiftRateNegotiationStatus.REJECTED;

  const onClick = () => {
    if (missingBackgroundCheckDataTypes.length > 0) {
      const url = createBackgroundCheckUrlFromMissingDataTypes({
        queryParams: new URLSearchParams(history.location.search),
        shiftId,
        missingBackgroundCheckDataTypes,
      });
      history.push(url);
      return;
    }

    if (requiresBreakPolicyAcknowledgement) {
      breakPolicyModalState.openModal();
    } else {
      navigateToRateNegotiationModal();
    }
  };

  return (
    <>
      <Button fullWidth size="large" variant="outlined" disabled={isDisabled} onClick={onClick}>
        {label}
      </Button>
      {requiresBreakPolicyAcknowledgement && (
        <BreakConfirmationBottomSheet
          action={NoteAcknowledgementAction.START_RATE_NEGOTIATION}
          shiftId={shiftId}
          workplaceId={workplaceId}
          breakPolicyNoteId={breakPolicyNoteId!}
          modalState={breakPolicyModalState}
          onAcknowledge={async () => {
            breakPolicyModalState.closeModal();
            navigateToRateNegotiationModal();
          }}
        />
      )}
    </>
  );
}
